import React from "react";
import { Button } from "@/components/ui/button";
import { Cpu, Sparkles, AlertOctagon, Wrench, Lightbulb, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function AIAdvicePanel({ advice, loading, onRequest }) {
  const { t } = useI18n();
  return (
    <div className="border border-[#D4D4D4] dark:border-[#1F1F1F] bg-[#F9F9FA] dark:bg-[#0F0F0F]" data-testid="ai-advice-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#141414]">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-[var(--neon-cyan)]"/>
          <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold">
            {t("ai.title", "AI Expert Analysis")}
          </span>
        </div>
        <Button onClick={onRequest} disabled={loading}
          className="rounded-none bg-[var(--neon-cyan)] hover:bg-[var(--neon-cyan)]/80 text-[#0A0A0A] h-8 px-3 font-mono text-[10px] uppercase tracking-wider"
          data-testid="run-advice-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin"/> {t("ai.btn.analyzing", "Analyzing...")}</> :
            <><Sparkles className="w-3 h-3 mr-1"/> {t("ai.btn.run", "Analyze")}</>}
        </Button>
      </div>

      <div className="p-4 space-y-4">
        {!advice && !loading && (
          <p className="font-mono leading-relaxed paragraph-balanced"
            style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
            {t("ai.intro", "Get an AI expert analysis of the current layers — alternative material suggestions, weaknesses, mitigation methods and installation tips.")}
          </p>
        )}
        {loading && (
          <div className="font-mono text-xs text-[var(--neon-cyan)]">
            &gt; {t("ai.thinking", "Claude Haiku 4.5 is analysing...")}
          </div>
        )}

        {advice && (
          <>
            <div className="border border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#141414] p-3" data-testid="advice-summary">
              <div className="flex items-center justify-between mb-2">
                <span className="tech-label">{t("ai.summary", "SUMMARY")}</span>
                <span className="font-mono text-2xl font-semibold">
                  {advice.puan}<span className="text-xs text-[#52525B]">/100</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed">{advice.ozet}</p>
            </div>

            {advice.oneri_malzemeler?.length > 0 && (
              <div data-testid="advice-recommendations">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#34C759]"/>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold">
                    {t("ai.recommended", "Recommended Materials")}
                  </span>
                </div>
                <ul className="space-y-2">
                  {advice.oneri_malzemeler.map((r, i) => (
                    <li key={i} className="border border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#141414] p-3">
                      <div className="font-semibold text-sm">{r.isim}</div>
                      <div className="text-xs text-[#52525B] mt-1">{r.neden}</div>
                      {r.tahmini_iyilesme && (
                        <div className="font-mono text-[11px] mt-2 text-[#34C759]">↑ {r.tahmini_iyilesme}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {advice.dezavantajlar?.length > 0 && (
              <div data-testid="advice-drawbacks">
                <div className="flex items-center gap-2 mb-2">
                  <AlertOctagon className="w-4 h-4 text-[#FF3B30]"/>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold">
                    {t("ai.drawbacks", "Weaknesses & Mitigations")}
                  </span>
                </div>
                <div className="space-y-2">
                  {advice.dezavantajlar.map((d, i) => (
                    <div key={i} className="border border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#141414] p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{d.malzeme}</div>
                        <span className="font-mono text-[10px] uppercase tracking-wider bg-[#FFE5E3] dark:bg-[#2A0F0F] text-[#FF3B30] px-2 py-0.5">
                          {d.zayiflik}
                        </span>
                      </div>
                      <p className="text-xs text-[#52525B] mt-1">{d.aciklama}</p>

                      {d.azaltma_yontemleri?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-[#E5E5E5] dark:border-[#1F1F1F]">
                          <div className="flex items-center gap-1 tech-label mb-1">
                            <Wrench className="w-3 h-3"/> {t("ai.mitigation", "MITIGATION")}
                          </div>
                          <ul className="space-y-1 text-xs">
                            {d.azaltma_yontemleri.map((s, k) => (
                              <li key={k} className="font-mono before:content-['→_'] before:neon-cyan">{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {d.tamamlayici_malzemeler?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {d.tamamlayici_malzemeler.map((tag, k) => (
                            <span key={k} className="font-mono text-[10px] bg-[#F2F2F2] dark:bg-[#0A0A0A] border border-[#D4D4D4] dark:border-[#1F1F1F] px-2 py-0.5">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {advice.uygulama_ipuclari?.length > 0 && (
              <div data-testid="advice-tips">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-[#FFCC00]"/>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold">
                    {t("ai.installTips", "Installation Tips")}
                  </span>
                </div>
                <ul className="space-y-1 border border-[#D4D4D4] dark:border-[#1F1F1F] bg-white dark:bg-[#141414] p-3">
                  {advice.uygulama_ipuclari.map((tip, i) => (
                    <li key={i} className="text-xs leading-relaxed font-mono before:content-['◇_'] before:neon-cyan">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
